import { EmptyState } from "@pushpress/pushpress-ui";
import {
  AlertCircle,
  Calendar,
  FileText,
  Flag,
  PartyPopper,
  Receipt,
  UserPlus,
} from "lucide-react";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { useLegacyNavigate } from "../../../hooks/useLegacyNavigate";
import { useNavigate } from "../../../hooks/useNavigate";
import { SentimentIcon } from "./SentimentIcon";
import { useContext } from "react";
import { AppContext } from "../../../context/app";
import { SHOW_PEOPLE_PAGE_FEATURE_FLAG } from "../../../context/features/features.utils";
import { useExperiment } from "../../../hooks/useExperiment";

const getEmptyStates = ({
  isPeoplePageEnabled,
}: {
  isPeoplePageEnabled: boolean;
}) => ({
  activated: {
    overdueInvoices: {
      title: "Great work!",
      description: "No members have overdue invoices.",
      icon: (
        <SentimentIcon
          sentiment="negative"
          icon={<Receipt size={28} className="tw-text-amber-500" />}
        />
      ),
    },
    openInvoices: {
      title: "Great work!",
      description: "No members have open invoices.",
      icon: (
        <SentimentIcon
          sentiment="negative"
          icon={<Receipt size={28} className="tw-text-amber-500" />}
        />
      ),
    },
    atRiskMembers: {
      title: "Great work!",
      description: "No members are at risk of cancelling.",
      icon: (
        <SentimentIcon
          sentiment="negative"
          icon={<AlertCircle size={28} className="tw-text-amber-500" />}
        />
      ),
    },
    expiringMembers: {
      title: "Great work!",
      description: "No members have expiring memberships.",
      icon: (
        <SentimentIcon
          sentiment="negative"
          icon={<Calendar size={28} className="tw-text-amber-500" />}
        />
      ),
    },
    newLeads: {
      title: "Opportunity awaits",
      description: "There are no new leads.",
      primaryActionLabel: "Add new leads",
      isLegacy: isPeoplePageEnabled,
      link: isPeoplePageEnabled ? "/people/leads" : "/members/leads",
      icon: (
        <SentimentIcon
          sentiment="positive"
          icon={<FileText size={28} className="tw-text-green-500" />}
        />
      ),
    },
    newMemberships: {
      title: "Opportunity awaits",
      description: "There are no new memberships.",
      primaryActionLabel: "Add new memberships",
      isLegacy: isPeoplePageEnabled,
      link: isPeoplePageEnabled ? "/people/members" : "/members/index",
      icon: (
        <SentimentIcon
          sentiment="positive"
          icon={<UserPlus size={28} className="tw-text-green-500" />}
        />
      ),
    },
    communications: {
      title: "No upcoming celebrations",
      icon: (
        <SentimentIcon
          sentiment="positive"
          icon={<PartyPopper size={28} className="tw-text-green-500" />}
        />
      ),
    },
    cancelRequests: {
      title: "Cancellation requests",
      description: "No pending cancellation requests.",
      icon: (
        <SentimentIcon
          sentiment="positive"
          icon={<Flag size={28} className="tw-text-green-500" />}
        />
      ),
    },
  },
  nonActivated: {
    overdueInvoices: {
      title: "Overdue invoices",
      description: "Here you'll find members with overdue invoices.",
      primaryActionLabel: "View all members",
      isLegacy: true,
      link: "/members",
      icon: (
        <SentimentIcon
          sentiment="negative"
          icon={<Receipt size={28} className="tw-text-amber-500" />}
        />
      ),
    },
    openInvoices: {
      title: "Open invoices",
      description: "Here you'll find members with open invoices.",
      primaryActionLabel: "View all members",
      isLegacy: true,
      link: "/members",
      icon: (
        <SentimentIcon
          sentiment="negative"
          icon={<Receipt size={28} className="tw-text-amber-500" />}
        />
      ),
    },
    atRiskMembers: {
      title: "At risk members",
      description:
        "Here you'll find members who haven't checked-in in a while.",
      primaryActionLabel: "View all members",
      isLegacy: true,
      link: "/members",
      icon: (
        <SentimentIcon
          sentiment="negative"
          icon={<AlertCircle size={28} className="tw-text-amber-500" />}
        />
      ),
    },
    expiringMembers: {
      title: "Expiring plans",
      description: "Here you'll find members with expiring plans.",
      primaryActionLabel: "Add new plans",
      isLegacy: false,
      link: "/plans/list",
      icon: (
        <SentimentIcon
          sentiment="negative"
          icon={<Calendar size={28} className="tw-text-amber-500" />}
        />
      ),
    },
    newLeads: {
      title: "New leads",
      description: "Here you'll find new leads.",
      primaryActionLabel: "Add new leads",
      isLegacy: true,
      link: "/members/leads",
      icon: (
        <SentimentIcon
          sentiment="positive"
          icon={<FileText size={28} className="tw-text-green-500" />}
        />
      ),
    },
    newMemberships: {
      title: "New memberships",
      description: "Here you'll find new memberships.",
      primaryActionLabel: "Add new plans",
      isLegacy: false,
      link: "/plans/list",
      icon: (
        <SentimentIcon
          sentiment="positive"
          icon={<UserPlus size={28} className="tw-text-green-500" />}
        />
      ),
    },
    communications: {
      title: "No upcoming celebrations",
      description: "Here you'll find upcoming celebrations.",
      icon: (
        <SentimentIcon
          sentiment="positive"
          icon={<PartyPopper size={28} className="tw-text-green-500" />}
        />
      ),
    },
    cancelRequests: {
      title: "Cancellation requests",
      description: "No pending cancellation requests.",
      icon: (
        <SentimentIcon
          sentiment="positive"
          icon={<Flag size={28} className="tw-text-green-500" />}
        />
      ),
    },
  },
});

export const SheetEmptyState = ({ type }: { type: string }) => {
  const { trackEvent } = useAnalytics();
  const { client } = useContext(AppContext);
  const { useExperimentValue } = useExperiment();
  const isPeoplePageEnabled = useExperimentValue(
    SHOW_PEOPLE_PAGE_FEATURE_FLAG,
    false,
  ) as boolean;

  const isActivated = client?.bankConnected === 1;

  const legacyNavigate = useLegacyNavigate();
  const navigate = useNavigate();
  const activated = isActivated ? "activated" : "nonActivated";

  const EmptyStates = getEmptyStates({ isPeoplePageEnabled });

  return (
    <EmptyState
      icon={EmptyStates[activated][type].icon}
      title={EmptyStates[activated][type].title}
      description={EmptyStates[activated][type].description}
      primaryActionLabel={
        EmptyStates[activated][type]?.primaryActionLabel
          ? EmptyStates[activated][type]?.primaryActionLabel
          : ""
      }
      primaryActionOnClick={() => {
        trackEvent("Dashboard Drawer Empty State Clicked", {
          type: type,
          link: EmptyStates[activated][type]?.link,
        });
        if (EmptyStates[activated][type]?.isLegacy) {
          legacyNavigate(EmptyStates[activated][type]?.link);
        } else {
          navigate(EmptyStates[activated][type]?.link);
        }
      }}
    />
  );
};
